/**
 * Shared mount / dismount API (boats, minecarts, horses). A vehicle implements
 * `Rideable`; the player and mobs sit in numbered seats. Whoever adds a new
 * rideable (MOBS-2 horses) implements `Rideable` and calls the same
 * `mountPlayer` / `dismountPlayer` helpers, so there is exactly one mount model.
 * DOM-free.
 */

/** Player input routed to the mount each tick (same abstraction as walking; touch controls synthesise the same keys). */
export interface RiderInput { forward: number; strafe: number; jump: boolean; sneak: boolean }

/** Anything a rider can be seated on. */
export interface Rideable {
  readonly id: number;
  readonly kind: 'boat' | 'minecart' | 'horse' | string;
  readonly seatCount: number;
  /** Rider pose flag for the player model / camera: seated riders sit lower. */
  readonly riderPose?: 'sit' | 'stand';
  /** Added to the rider's eye height (negative = seated). */
  readonly riderEyeOffset?: number;
  /** Seat 0's rider drives: called once per game tick with the player's input. */
  steer?(input: RiderInput): void;
  /** Rider in seat `i` (null = free). Players use the PLAYER token, mobs their object. */
  riders: (unknown | null)[];
  /** World position (feet) of seat `i`. */
  seatPos(i: number, out: { x: number; y: number; z: number }): void;
  /** Where a rider is put when it leaves (nearest free standing spot). */
  dismountPos(out: { x: number; y: number; z: number }): void;
}

/** The token that stands for the local player in `Rideable.riders`. */
export const PLAYER = { player: true } as const;

/** The vehicle the local player is sitting in (null on foot). */
export const ride: { current: Rideable | null; seat: number } = { current: null, seat: 0 };

export function freeSeat(v: Rideable): number {
  for (let i = 0; i < v.seatCount; i++) if (v.riders[i] == null) return i;
  return -1;
}

/** Seat the player (seat 0 preferred); false when the vehicle is full. */
export function mountPlayer(v: Rideable): boolean {
  if (ride.current) return false;
  const s = v.riders[0] == null ? 0 : freeSeat(v);
  if (s < 0) return false;
  v.riders[s] = PLAYER;
  ride.current = v; ride.seat = s;
  return true;
}

/** Seat any mob-like rider (identity is the object); false when full. */
export function mountMob(v: Rideable, who: unknown): boolean {
  if (v.riders.includes(who)) return true;
  const s = freeSeat(v);
  if (s < 0) return false;
  v.riders[s] = who;
  return true;
}

export function dismountMob(v: Rideable, who: unknown): void {
  const i = v.riders.indexOf(who);
  if (i >= 0) v.riders[i] = null;
}

/** Player leaves; returns the vehicle left (so the caller can place the player at its dismountPos). */
export function dismountPlayer(): Rideable | null {
  const v = ride.current;
  if (!v) return null;
  const i = v.riders.indexOf(PLAYER);
  if (i >= 0) v.riders[i] = null;
  ride.current = null;
  return v;
}
